/**
 * The health questionnaire, on the patient detail page.
 *
 * This is what makes page 2 of the report work. Clinical patterns draw on
 * reported symptoms, and the Therapy Intelligence Library withholds every
 * suggestion unless at least one stated goal or symptom aligns with it (RR-03) -
 * so a patient with no intake gets a shorter report rather than a broken one, and
 * the nurse needs to be able to see that and fix it.
 *
 * Two deliberate choices in the UI:
 *
 *   1. Goals and symptoms are picked from a fixed list rather than typed free.
 *      The matcher works on a closed tag vocabulary, and an unrecognised phrase
 *      is dropped rather than guessed at. Letting a nurse type "wants to feel
 *      less puffy" and silently ignoring it would be worse than not offering the
 *      box.
 *   2. History and medications ARE free text, because they only ever suppress a
 *      therapy. Constraining them to a vocabulary could drop a term and
 *      un-suppress something contraindicated, which is the one failure that
 *      must not happen.
 */

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { Button, Card, Field, inputClass, LoadingRows, Toggle } from './ui';

/* The tag vocabulary, mirroring `INTAKE_GOALS` / `INTAKE_SYMPTOMS` in
 * `@mcos/therapy`. Grouped for scanning rather than listed alphabetically.
 * The API re-normalises whatever it receives, so this list is a convenience,
 * never the authority. */
const GOAL_GROUPS: Array<{ label: string; tags: string[] }> = [
  { label: 'Body composition', tags: ['weight loss', 'visceral fat reduction', 'body composition', 'lean muscle'] },
  { label: 'Energy & recovery', tags: ['energy', 'recovery', 'performance', 'sleep'] },
  { label: 'Mind', tags: ['focus', 'cognition', 'stress resilience', 'mood'] },
  { label: 'Metabolic & liver', tags: ['metabolic health', 'insulin sensitivity', 'lipid health', 'liver health', 'detoxification'] },
  { label: 'Longevity & appearance', tags: ['healthy aging', 'longevity', 'immune resilience', 'skin', 'hair'] },
  { label: 'Other', tags: ['sexual wellness', 'libido', 'injury recovery', 'tissue repair'] },
];

const SYMPTOM_GROUPS: Array<{ label: string; tags: string[] }> = [
  { label: 'Energy', tags: ['fatigue', 'low energy', 'post-viral fatigue'] },
  { label: 'Cognition', tags: ['brain fog', 'mental fatigue', 'focus concerns'] },
  { label: 'Sleep & stress', tags: ['poor sleep', 'sleep disturbance', 'stress', 'anxiety'] },
  { label: 'Recovery', tags: ['poor recovery', 'muscle soreness', 'muscle weakness', 'exercise intolerance'] },
  { label: 'Body', tags: ['central adiposity', 'weight-loss plateau', 'joint pain', 'muscle cramps', 'migraines', 'dehydration'] },
  { label: 'Other', tags: ['frequent illness', 'low libido', 'arousal concerns', 'skin aging', 'hair thinning', 'gi symptoms'] },
];

interface Intake {
  goals: string[];
  symptoms: string[];
  history: string[];
  medications: string[];
  previousTherapies: Array<{ name: string; response: 'good' | 'poor' | 'unknown' }>;
  chiefComplaint: string | null;
  primaryGoal: string | null;
  safetyScreenComplete: boolean;
  heightIn: number | null;
  weightLb: number | null;
  waistIn: number | null;
  updatedAt: string;
}

const EMPTY = {
  goals: [] as string[],
  symptoms: [] as string[],
  history: '',
  medications: '',
  chiefComplaint: '',
  primaryGoal: '',
  safetyScreenComplete: false,
  heightIn: '',
  weightLb: '',
  waistIn: '',
};

export function IntakeCard({ patientId, canEdit }: { patientId: string; canEdit: boolean }) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState(EMPTY);
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['intake', patientId],
    queryFn: () => api.get<{ intake: Intake | null }>(`/patients/${patientId}/intake`),
  });

  const intake = query.data?.intake ?? null;

  // Seed the editor from the server once, and again whenever the record changes
  // underneath us. Guarded on `open` so it cannot clobber a half-typed edit.
  useEffect(() => {
    if (open) return;
    setDraft(
      intake
        ? {
            goals: intake.goals,
            symptoms: intake.symptoms,
            history: intake.history.join('\n'),
            medications: intake.medications.join('\n'),
            chiefComplaint: intake.chiefComplaint ?? '',
            primaryGoal: intake.primaryGoal ?? '',
            safetyScreenComplete: intake.safetyScreenComplete,
            heightIn: intake.heightIn != null ? String(intake.heightIn) : '',
            weightLb: intake.weightLb != null ? String(intake.weightLb) : '',
            waistIn: intake.waistIn != null ? String(intake.waistIn) : '',
          }
        : EMPTY,
    );
  }, [intake, open]);

  const save = useMutation({
    mutationFn: () =>
      api.put<{ unmapped: { goals: number; symptoms: number } }>(
        `/patients/${patientId}/intake`,
        {
          goals: draft.goals,
          symptoms: draft.symptoms,
          history: splitLines(draft.history),
          medications: splitLines(draft.medications),
          chiefComplaint: draft.chiefComplaint || null,
          primaryGoal: draft.primaryGoal || null,
          safetyScreenComplete: draft.safetyScreenComplete,
          heightIn: numOrNull(draft.heightIn),
          weightLb: numOrNull(draft.weightLb),
          waistIn: numOrNull(draft.waistIn),
        },
      ),
    onSuccess: (result) => {
      setError(null);
      const dropped = result.unmapped.goals + result.unmapped.symptoms;
      setMessage(
        dropped > 0
          ? `Saved, but ${dropped} entr${dropped === 1 ? 'y was' : 'ies were'} not recognised and have been left out.`
          : 'Saved. The next generated report will use this.',
      );
      setOpen(false);
      void queryClient.invalidateQueries({ queryKey: ['intake', patientId] });
    },
    onError: (caught) => {
      setMessage(null);
      setError(caught instanceof Error ? caught.message : 'Could not save');
    },
  });

  if (query.isLoading) return <LoadingRows rows={4} />;

  const toggleTag = (field: 'goals' | 'symptoms', tag: string) => {
    const current = draft[field];
    setDraft({
      ...draft,
      [field]: current.includes(tag) ? current.filter((t) => t !== tag) : [...current, tag],
    });
  };

  /* --- Read-only summary ------------------------------------------- */
  if (!open) {
    return (
      <Card
        title="Health questionnaire"
        subtitle="Drives the clinical patterns and therapy suggestions on page 2 of the report."
        action={
          canEdit ? (
            <Button variant="secondary" onClick={() => setOpen(true)}>
              {intake ? 'Edit' : 'Add'}
            </Button>
          ) : undefined
        }
      >
        {message && (
          <div className="mb-3 rounded-sm2 border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800">
            {message}
          </div>
        )}

        {!intake ? (
          <div className="rounded-sm2 border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs leading-relaxed text-amber-900">
            <strong className="font-semibold">No questionnaire on file.</strong> Reports for
            this patient will still show wellness scores and any patterns visible in the
            labs, but no therapy suggestions, those need at least one stated goal or
            symptom.
          </div>
        ) : (
          <dl className="space-y-3 text-sm">
            {(intake.heightIn != null || intake.weightLb != null || intake.waistIn != null) && (
              <div>
                <dt className="text-xs font-medium uppercase tracking-wide text-ink-muted">
                  Measurements
                </dt>
                <dd className="mt-1 flex flex-wrap gap-3 text-ink">
                  {intake.heightIn != null && <span>Height {intake.heightIn} in</span>}
                  {intake.weightLb != null && <span>Weight {intake.weightLb} lb</span>}
                  {intake.waistIn != null && <span>Waist {intake.waistIn} in</span>}
                </dd>
                <dd className="mt-0.5 text-[11px] text-ink-muted">
                  BMI and waist-to-height ratio are computed on the report; an InBody scan is
                  not required.
                </dd>
              </div>
            )}
            <SummaryRow label="Goals" values={intake.goals} />
            <SummaryRow label="Symptoms" values={intake.symptoms} />
            <SummaryRow label="Medical history" values={intake.history} muted />
            <SummaryRow label="Medications" values={intake.medications} muted />
            {intake.chiefComplaint && (
              <div>
                <dt className="text-xs font-medium uppercase tracking-wide text-ink-muted">
                  Chief complaint
                </dt>
                <dd className="mt-1 text-ink">{intake.chiefComplaint}</dd>
              </div>
            )}
            <div className="flex items-center gap-2 border-t border-ink/5 pt-3">
              <span
                className={`inline-block h-2 w-2 rounded-full ${
                  intake.safetyScreenComplete ? 'bg-emerald-500' : 'bg-amber-500'
                }`}
              />
              <span className="text-xs text-ink-muted">
                {intake.safetyScreenComplete
                  ? 'Safety screen complete.'
                  : 'Safety screen incomplete, therapies with contraindications are held back.'}
              </span>
            </div>
          </dl>
        )}
      </Card>
    );
  }

  /* --- Editor ------------------------------------------------------- */
  return (
    <Card
      title="Health questionnaire"
      subtitle="Goals and symptoms come from a fixed list, because that is what the therapy matcher understands."
    >
      {error && (
        <div className="mb-3 rounded-sm2 border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">
          {error}
        </div>
      )}

      <div className="space-y-5">
        <TagPicker
          legend="Primary wellness goals"
          groups={GOAL_GROUPS}
          selected={draft.goals}
          onToggle={(tag) => toggleTag('goals', tag)}
        />
        <TagPicker
          legend="Current symptoms"
          groups={SYMPTOM_GROUPS}
          selected={draft.symptoms}
          onToggle={(tag) => toggleTag('symptoms', tag)}
        />

        <fieldset>
          <legend className="text-xs font-medium uppercase tracking-wide text-ink-muted">
            Measurements
          </legend>
          <p className="mt-1 mb-2 text-[11px] leading-relaxed text-ink-muted">
            Height and weight give every patient a Body Composition &amp; Adiposity assessment
            (BMI) without an InBody scanner. Waist is optional but strongly encouraged, it adds
            the waist-to-height ratio.
          </p>
          <div className="grid grid-cols-3 gap-2">
            <Field label="Height (in)">
              <input
                type="number" inputMode="decimal" step="0.1" min={24} max={96}
                className={inputClass}
                value={draft.heightIn}
                onChange={(event) => setDraft({ ...draft, heightIn: event.target.value })}
              />
            </Field>
            <Field label="Weight (lb)">
              <input
                type="number" inputMode="decimal" step="0.1" min={40} max={1500}
                className={inputClass}
                value={draft.weightLb}
                onChange={(event) => setDraft({ ...draft, weightLb: event.target.value })}
              />
            </Field>
            <Field label="Waist (in)" hint="Optional">
              <input
                type="number" inputMode="decimal" step="0.1" min={15} max={90}
                className={inputClass}
                value={draft.waistIn}
                onChange={(event) => setDraft({ ...draft, waistIn: event.target.value })}
              />
            </Field>
          </div>
        </fieldset>

        <Field
          label="Medical history"
          hint="One per line. Stored exactly as written, these suppress therapies, so nothing is normalised away."
        >
          <textarea
            className={`${inputClass} min-h-[76px]`}
            value={draft.history}
            placeholder={'active malignancy\npregnancy\nseizure disorder'}
            onChange={(event) => setDraft({ ...draft, history: event.target.value })}
          />
        </Field>

        <Field label="Current medications" hint="One per line.">
          <textarea
            className={`${inputClass} min-h-[76px]`}
            value={draft.medications}
            onChange={(event) => setDraft({ ...draft, medications: event.target.value })}
          />
        </Field>

        <Field label="Chief complaint" hint="In the patient's own words. Also read for symptoms.">
          <textarea
            className={`${inputClass} min-h-[60px]`}
            value={draft.chiefComplaint}
            onChange={(event) => setDraft({ ...draft, chiefComplaint: event.target.value })}
          />
        </Field>

        <Toggle
          checked={draft.safetyScreenComplete}
          onChange={(next) => setDraft({ ...draft, safetyScreenComplete: next })}
          label="Safety screen complete"
          description="Confirms a clinician has taken the contraindication history above. Until this is on, every therapy carrying a contraindication is held back from the report."
        />
      </div>

      <div className="mt-5 flex items-center gap-2 border-t border-ink/5 pt-4">
        <Button loading={save.isPending} onClick={() => save.mutate()}>
          Save questionnaire
        </Button>
        <Button variant="secondary" onClick={() => setOpen(false)}>
          Cancel
        </Button>
      </div>
    </Card>
  );
}

function SummaryRow({
  label,
  values,
  muted = false,
}: {
  label: string;
  values: string[];
  muted?: boolean;
}) {
  return (
    <div>
      <dt className="text-xs font-medium uppercase tracking-wide text-ink-muted">{label}</dt>
      <dd className="mt-1 flex flex-wrap gap-1.5">
        {values.length === 0 ? (
          <span className="text-xs text-ink-muted">None recorded</span>
        ) : (
          values.map((value) => (
            <span
              key={value}
              className={`rounded-full px-2 py-0.5 text-xs ${
                muted ? 'bg-ink/5 text-ink' : 'bg-slate-900 text-white'
              }`}
            >
              {value}
            </span>
          ))
        )}
      </dd>
    </div>
  );
}

function TagPicker({
  legend,
  groups,
  selected,
  onToggle,
}: {
  legend: string;
  groups: Array<{ label: string; tags: string[] }>;
  selected: string[];
  onToggle: (tag: string) => void;
}) {
  return (
    <fieldset>
      <legend className="text-xs font-medium uppercase tracking-wide text-ink-muted">
        {legend}
      </legend>
      <div className="mt-2 space-y-2.5">
        {groups.map((group) => (
          <div key={group.label}>
            <div className="mb-1 text-[11px] text-ink-muted">{group.label}</div>
            <div className="flex flex-wrap gap-1.5">
              {group.tags.map((tag) => {
                const active = selected.includes(tag);
                return (
                  <button
                    key={tag}
                    type="button"
                    aria-pressed={active}
                    onClick={() => onToggle(tag)}
                    className={`rounded-full border px-2.5 py-1 text-xs transition ${
                      active
                        ? 'border-slate-900 bg-slate-900 text-white'
                        : 'border-ink/10 bg-white text-ink hover:border-slate-400'
                    }`}
                  >
                    {tag}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </fieldset>
  );
}

/** Split a textarea into trimmed, non-empty lines. */
/** Parse a numeric text field to a number, or null when blank/invalid. */
function numOrNull(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed === '') return null;
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : null;
}

function splitLines(value: string): string[] {
  return value
    .split(/[\n,;]+/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
}
